import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-when-you-cant-pray",
  title: "A prayer for when you cannot pray",
  h1: "A prayer for when you cannot pray",
  description:
    "For the weeks when prayer has gone silent or empty: a short prayer that does not require you to feel anything, and what Romans 8 says is happening meanwhile.",
  targets: [
    "prayer when you cant pray",
    "what to do when you cannot pray",
    "prayer for when god feels silent",
    "prayer when you have no words",
  ],
  published: "2026-09-03",
  standfirst:
    "Sometimes the words stop. Not because of a decision, and usually not because of a sin you can name — they simply stop.",
  takeaways: [
    "You do not have to feel anything for this to count. Prayer is an address, not a mood, and the psalms are full of people praying badly on purpose.",
    "Romans 8:26 says the Spirit is already praying in the gap — the not knowing how is described as the normal condition, not the failure state.",
    "A dry season is not evidence of anything. Almost every writer on prayer in Christian history describes one, usually at length.",
  ],
  when: [
    "In week three of nothing, when you have stopped expecting anything to happen.",
    "After something you cannot make sense of, when praying about it feels like pretending.",
    "When you are angry and every honest sentence would be a complaint.",
    "When you sit down to pray and immediately reach for your phone instead.",
  ],
  prayer: [
    "God, I have nothing. I am not going to manufacture something and call it prayer, so this is what there is.",
    "I do not know whether I am angry with you or embarrassed or just tired. Take it as it comes; you can sort out which it was.",
    "If you are speaking and I have stopped hearing it, keep speaking. If you are silent, I would rather sit here in the silence than fill it with noise.",
    "The Spirit is supposed to be praying what I cannot. I am relying on that entirely this week, because I have nothing else to bring.",
    "I am not asking to feel better about this. I am asking to still be here tomorrow, saying this again.",
  ],
  short: "God, I have nothing. I am here anyway. Take it as it comes.",
  honest: [
    "This does not promise the dryness will lift on a schedule. Some of it lasts a long time, and the people who wrote most usefully about it were describing years rather than weeks.",
    "It also does not diagnose the cause. Sometimes the silence follows a grief, sometimes exhaustion, sometimes something you already know needs dealing with — and a page cannot tell you which, so it should not pretend to.",
    "If nothing has felt like anything for months, in prayer or out of it, that is worth saying to a doctor as well as to God. Depression flattens everything, and it flattens prayer first.",
  ],
  verses: [
    {
      verse: {
        text: "In the same way, the Spirit also helps our weaknesses, for we don't know how to pray as we ought. But the Spirit himself makes intercession for us with groanings which can't be uttered.",
        ref: "Romans 8:26",
      },
      note: "The sentence assumes not knowing how to pray is the standard condition of a Christian, not a lapse — Paul writes we, and he includes himself. And the help described is not instruction in better technique. Something else is doing the praying, in a register with no words in it, which is exactly the register you are in when you cannot find any.",
    },
    {
      verse: {
        text: "Trust in him at all times, you people. Pour out your heart before him. God is a refuge for us. Selah.",
        ref: "Psalm 62:8",
      },
      note: "Pour out is a word for emptying a container completely, including the sediment. It is not an instruction to bring your best material. The psalm has already spent several verses on people who lie to your face and bless you while cursing inwardly, so the heart being poured out here is not in good condition.",
    },
    {
      verse: {
        text: "Why are you in despair, my soul? Why are you disturbed within me? Hope in God! For I shall still praise him, the saving help of my countenance, and my God.",
        ref: "Psalm 42:11",
      },
      note: "A man talking to himself because talking to God is not currently working — and the same two questions have already been asked twice earlier in the psalm without being resolved. The repetition is the point. This is not a man who cheered up; it is a man saying the true thing again on a day he does not feel it.",
    },
    {
      verse: {
        text: "In praying, don't use vain repetitions as the Gentiles do; for they think that they will be heard for their much speaking. Therefore don't be like them, for your Father knows what things you need before you ask him.",
        ref: "Matthew 6:7–8",
      },
      note: "Aimed at people praying too much rather than too little, which makes it unexpectedly useful here. If you are heard on the basis of volume, having nothing to say is a catastrophe. If you are heard by someone who already knows, then a short, empty-handed prayer is not a lesser one — it is the kind the passage is recommending.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Dryness is not a verdict",
      id: "dryness-is-not-a-verdict",
    },
    {
      type: "p",
      text: "The first thing most people do when prayer goes quiet is treat it as evidence — that they have done something, that they never really believed, that it was always self-talk. That reading is almost never supported by the tradition, which treats dry seasons as ordinary, expected, and frequently a phase in something rather than the end of it.",
    },
    {
      type: "p",
      text: "The psalms are the strongest argument available. Nearly a third of them are complaints, several end without resolution, and they were kept in the book of prayers rather than edited out. A tradition that files unanswered, joyless prayer as normal is not one that thinks silence is a disqualification.",
    },
    {
      type: "h2",
      text: "Keep the shape when the content goes",
      id: "keep-the-shape-when-the-content-goes",
    },
    {
      type: "p",
      text: "The practical advice that survives contact with a real dry spell is unglamorous: keep the time and the place even after the words go. Sit down at the same point in the day. Say a psalm you did not write. Stop when the time is up.",
    },
    {
      type: "p",
      text: "This is the same principle as {memorising verses} — a form you can occupy on the days you have nothing to bring to it. The form is not hypocrisy. It is the thing that is still standing when the feeling comes back.",
      links: {
        "memorising verses": "/blog/how-to-memorize-bible-verses",
      },
    },
    {
      type: "note",
      text: "If the reason you cannot pray is that the moment you sit down you reach for your phone, that is a different problem with an easier fix. Put the phone in another room and see whether the silence is still empty.",
    },
    {
      type: "h2",
      text: "Somebody else is praying",
      id: "somebody-else-is-praying",
    },
    {
      type: "p",
      text: "Romans 8:26 is the most practically useful sentence in the New Testament for this week. It does not say try harder or wait for it to pass. It says the praying is happening, in you, without words, and that your inability to do it properly was accounted for.",
    },
    {
      type: "p",
      text: "Which means the honest prayer here is short and does not have to be good. Show up, say there is nothing, stay a minute, leave. Do that for six weeks and something usually changes — and if it does not, you have still spent six weeks in the room.",
    },
  ],
  faq: [
    {
      q: "What do you pray when you have no words?",
      a: '"God, I have nothing. I am here anyway." Romans 8:26 says the Spirit is already praying what you cannot put into words, so a short and empty-handed prayer is not a lesser one.',
    },
    {
      q: "Does God still hear prayer when I feel nothing?",
      a: "Feeling is not the mechanism. Nearly a third of the psalms are complaints and several end unresolved; they were kept in the prayer book of Israel rather than edited out, which is the strongest available evidence that joyless prayer still counts as prayer.",
    },
    {
      q: "How long do dry seasons in prayer last?",
      a: "There is no schedule, and anyone promising one is guessing. What the tradition consistently recommends is keeping the time and the place after the words have gone, on the grounds that the form is what the feeling comes back to.",
    },
  ],
  relatedPrayers: ["prayer-for-anxiety", "morning-prayer"],
  related: ["how-to-memorize-bible-verses", "morning-quiet-time-routine"],
  relatedTopics: [
    "bible-verses-about-grief",
    "bible-verses-about-strength",
    "bible-verses-about-prayer",
  ],
  relatedPassages: ["jeremiah-29-11", "psalm-91"],
};
